import type { I18n } from '@/lib/api/types';
import { arCompleteness } from '@/lib/i18n/content';

/**
 * Page-copy content is a free-form JSONB tree whose *leaves* are bilingual `{ en, ar }`
 * I18n objects (the public `/page-copy/{ns}` endpoint resolves each leaf to one locale).
 * These pure helpers let the editor render that tree as paired EN/AR fields instead of raw
 * JSON, track Arabic coverage, and emit a minimal RFC 7396 Merge Patch for the PATCH route.
 */

export type Json =
  | string
  | number
  | boolean
  | null
  | Json[]
  | { [key: string]: Json };

export type NodeKind = 'i18n' | 'group' | 'list' | 'text' | 'number' | 'boolean' | 'null';

/** Path from the content root: object keys (string) and array indices (number). */
export type Path = ReadonlyArray<string | number>;

export function isPlainObject(v: unknown): v is Record<string, Json> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** A leaf is an `{ en, ar }` pair: a plain object keyed only by `en`/`ar` with string values. */
export function isI18nLeaf(v: unknown): v is I18n {
  if (!isPlainObject(v)) return false;
  const keys = Object.keys(v);
  if (keys.length === 0) return false;
  if (!keys.every((k) => k === 'en' || k === 'ar')) return false;
  return keys.every((k) => typeof v[k] === 'string');
}

export function asI18n(v: unknown): I18n {
  if (isI18nLeaf(v)) return { en: v.en ?? '', ar: v.ar ?? '' };
  return { en: '', ar: '' };
}

export function classify(v: Json): NodeKind {
  if (v === null) return 'null';
  if (Array.isArray(v)) return 'list';
  if (isI18nLeaf(v)) return 'i18n';
  if (isPlainObject(v)) return 'group';
  if (typeof v === 'number') return 'number';
  if (typeof v === 'boolean') return 'boolean';
  return 'text';
}

// ── Arabic-coverage accounting ────────────────────────────────────────────────
export interface Coverage {
  /** I18n leaves that have English content (the translatable surface). */
  total: number;
  /** …of those, how many also have Arabic content. */
  translated: number;
}

/**
 * Page-copy documents come in two shapes:
 *   • **locale-first** — `{ en: {…tree…}, ar: {…tree…} }` — two parallel trees of plain-string
 *     leaves (this is what the public `/page-copy/{ns}` endpoint stores for most namespaces);
 *   • **bilingual-leaf** — `{ field: { en, ar }, … }` — the same `{en,ar}` leaves used across
 *     the rest of the console.
 *
 * The earlier implementation only understood the bilingual-leaf shape via {@link arCompleteness},
 * so locale-first namespaces (e.g. `hero`) reported "no translatable fields" even when fully
 * populated (FE-SETTINGS-1). We now detect the locale-first shape and count its EN leaves against
 * their AR counterparts; everything else falls back to the canonical leaf accounting so the
 * numbers still match the app-wide "missing Arabic" filter.
 */
export function coverage(node: Json): Coverage {
  if (isLocaleFirst(node)) return localeFirstCoverage(node);
  const { total, missing } = arCompleteness(node);
  return { total, translated: total - missing };
}

/** Locale-first: an object keyed only by `en`/`ar` whose values are nested trees, not strings. */
function isLocaleFirst(node: Json): node is Record<'en' | 'ar', Json> {
  if (!isPlainObject(node)) return false;
  const keys = Object.keys(node);
  if (keys.length === 0 || !keys.every((k) => k === 'en' || k === 'ar')) return false;
  // A single bilingual leaf `{en:'x', ar:'y'}` has string values — that's NOT locale-first.
  return keys.some((k) => isPlainObject(node[k]) || Array.isArray(node[k]));
}

/** Count EN string leaves (the translatable surface) against the AR leaf at the same path. */
function localeFirstCoverage(node: Record<'en' | 'ar', Json>): Coverage {
  let total = 0;
  let translated = 0;
  const walk = (en: Json, ar: Json | undefined): void => {
    if (typeof en === 'string') {
      if (en.trim()) {
        total += 1;
        if (typeof ar === 'string' && ar.trim()) translated += 1;
      }
      return;
    }
    if (Array.isArray(en)) {
      en.forEach((child, i) => walk(child, Array.isArray(ar) ? ar[i] : undefined));
    } else if (isPlainObject(en)) {
      for (const [k, child] of Object.entries(en)) {
        walk(child, isPlainObject(ar) ? ar[k] : undefined);
      }
    }
  };
  if (node.en !== undefined) walk(node.en, node.ar);
  return { total, translated };
}

/** Whole-tree Arabic completeness — true once every translatable leaf has Arabic. */
export function isArComplete(node: Json): boolean {
  const c = coverage(node);
  return c.total > 0 && c.translated === c.total;
}

// ── Immutable tree edits ──────────────────────────────────────────────────────
function cloneShallow(v: Json): Json {
  if (Array.isArray(v)) return v.slice();
  if (isPlainObject(v)) return { ...v };
  return v;
}

/** Return a copy of `root` with `value` set at `path` (creating nothing beyond the path). */
export function setAtPath(root: Json, path: Path, value: Json): Json {
  if (path.length === 0) return value;
  const [head, ...rest] = path;
  const next = cloneShallow(root);
  if (Array.isArray(next) && typeof head === 'number') {
    next[head] = setAtPath(next[head] ?? null, rest, value);
  } else if (isPlainObject(next) && typeof head === 'string') {
    next[head] = setAtPath(next[head] ?? null, rest, value);
  }
  return next;
}

/** Return a copy of `root` with the entry at `path` removed (object key or array index). */
export function deleteAtPath(root: Json, path: Path): Json {
  if (path.length === 0) return root;
  const [head, ...rest] = path;
  const next = cloneShallow(root);
  if (rest.length === 0) {
    if (Array.isArray(next) && typeof head === 'number') next.splice(head, 1);
    else if (isPlainObject(next) && typeof head === 'string') delete next[head];
    return next;
  }
  if (Array.isArray(next) && typeof head === 'number') {
    next[head] = deleteAtPath(next[head], rest);
  } else if (isPlainObject(next) && typeof head === 'string') {
    next[head] = deleteAtPath(next[head], rest);
  }
  return next;
}

/** Read the value at `path`, or `undefined` if the path doesn't resolve. */
export function getAtPath(root: Json, path: Path): Json | undefined {
  let cur: Json | undefined = root;
  for (const seg of path) {
    if (Array.isArray(cur) && typeof seg === 'number') cur = cur[seg];
    else if (isPlainObject(cur) && typeof seg === 'string') cur = cur[seg];
    else return undefined;
  }
  return cur;
}

/** Rename a key inside an object while preserving insertion order. No-op on collisions. */
export function renameKey(
  obj: Record<string, Json>,
  oldKey: string,
  newKey: string,
): Record<string, Json> {
  if (oldKey === newKey) return obj;
  if (newKey in obj) return obj; // refuse to clobber a sibling
  const next: Record<string, Json> = {};
  for (const [k, v] of Object.entries(obj)) next[k === oldKey ? newKey : k] = v;
  return next;
}

/** Move an array item from `index` by `delta` (clamped). Returns a new array. */
export function moveItem(list: Json[], index: number, delta: number): Json[] {
  const to = index + delta;
  if (to < 0 || to >= list.length) return list;
  const next = list.slice();
  const [item] = next.splice(index, 1);
  next.splice(to, 0, item);
  return next;
}

// ── Field paths (deep-link addressing) ────────────────────────────────────────
/**
 * Serialize a content path to dot notation, e.g. `["cta","heading"]` → `"cta.heading"`,
 * `["links",0,"label"]` → `"links.0.label"`. This is the canonical `field_path` the global
 * search uses to deep-link to one field. Object keys are slugs and array steps are numeric,
 * so the encoding round-trips for all real page-copy shapes.
 */
export function pathToString(path: Path): string {
  return path.map(String).join('.');
}

export function pathFromString(s: string): Path {
  if (!s) return [];
  return s
    .split('.')
    .filter((seg) => seg.length > 0)
    .map((seg) => (/^\d+$/.test(seg) ? Number(seg) : seg));
}

// ── RFC 7396 JSON Merge Patch ─────────────────────────────────────────────────
const UNCHANGED = Symbol('unchanged');

function deepEqual(a: Json, b: Json): boolean {
  if (a === b) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((x, i) => deepEqual(x, b[i]));
  }
  if (isPlainObject(a) && isPlainObject(b)) {
    const ka = Object.keys(a);
    const kb = Object.keys(b);
    return ka.length === kb.length && ka.every((k) => k in b && deepEqual(a[k], b[k]));
  }
  return false;
}

function diff(orig: Json, edited: Json): Json | typeof UNCHANGED {
  if (isPlainObject(orig) && isPlainObject(edited)) {
    const patch: Record<string, Json> = {};
    const keys = new Set([...Object.keys(orig), ...Object.keys(edited)]);
    for (const k of keys) {
      if (!(k in edited)) {
        patch[k] = null; // deletion
      } else if (!(k in orig)) {
        patch[k] = edited[k]; // addition (whole value)
      } else {
        const sub = diff(orig[k], edited[k]);
        if (sub !== UNCHANGED) patch[k] = sub as Json;
      }
    }
    return Object.keys(patch).length ? patch : UNCHANGED;
  }
  return deepEqual(orig, edited) ? UNCHANGED : edited;
}

/**
 * Minimal merge patch turning `orig` into `edited` (`null` deletes a key; scalars/arrays
 * replace wholesale). Returns `null` when nothing changed, so callers can skip the request.
 */
export function mergePatch(orig: Json, edited: Json): Record<string, Json> | null {
  const result = diff(orig ?? {}, edited ?? {});
  if (result === UNCHANGED) return null;
  return (isPlainObject(result) ? result : {}) as Record<string, Json>;
}

// ── Human-readable field labels ───────────────────────────────────────────────
/** Field keys that read better fully uppercased than title-cased. */
const ACRONYMS = new Set(['cta', 'url', 'seo', 'faq', 'id', 'eta', 'kpi', 'pdf', 'api']);

/**
 * Turn a raw content key into a human label for the editor, e.g. `cta_heading` → "CTA heading",
 * `heroTitle` → "Hero title", `footer-note` → "Footer note". The raw key is still shown beside
 * the label (it's what the search `field_path` addresses), so this is purely cosmetic.
 */
export function humanizeKey(key: string): string {
  const words = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2') // split camelCase
    .replace(/[_-]+/g, ' ') // snake_case / kebab-case → spaces
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0) return key;
  return words
    .map((w, i) => {
      const lower = w.toLowerCase();
      if (ACRONYMS.has(lower)) return w.toUpperCase();
      // Capitalize only the first word; keep the rest lower for a natural sentence case.
      return i === 0 ? lower.charAt(0).toUpperCase() + lower.slice(1) : lower;
    })
    .join(' ');
}
